import { IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@hortis/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { Plus, XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { toast } from "@hortis/ui/sonner";
import { Tag } from "@hortis/ui/tag";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  type RecordTagFilters,
  useCollectionSiteTagsQuery,
  useCreateRecordTagMutation,
  useGetCollectionSiteFromNamesQuery,
  useOrganisationTagsQuery,
} from "generated/graphql";
import { type ReactElement, useState } from "react";
import { useNavSites } from "src/components/nav-layout/site-context/use-nav-sites";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import type { TagOptionZod } from "src/features/create-accession/components/add-plant-material-form/schema";

export const TagMultiPickerLabel = ({
  value,
}: { value: Array<TagOptionZod> }) => (
  <div className="flex flex-wrap items-center gap-2 truncate">
    {value.map((tag) => (
      <Tag
        key={tag.id}
        spacing="icon"
        data-cy="tag-picker-tag"
        className="block truncate"
      >
        {tag.name}
      </Tag>
    ))}
  </div>
);

export const TagMultiPicker = ({
  value,
  onChange,
  tagLevel = "org",
  newTagOption = false,
  tagFilters,
  placeholder,
  children,
  "data-cy": dataCy,
}: {
  value: Array<TagOptionZod> | undefined | null;
  onChange: (val: Array<TagOptionZod> | undefined | null) => void;
  tagLevel?: "site" | "org";
  newTagOption?: boolean;
  tagFilters?: RecordTagFilters;
  placeholder?: string;
  children: ReactElement;
  "data-cy"?: string;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const isDesktop = useMediaQuery("md");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [{ fetching: createTagFetching }, createTag] =
    useCreateRecordTagMutation();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const settingsSites = useNavSites();
  const slug = siteSlug ?? settingsSites.activeSite?.urlSlug;

  const [{ data: siteData }] = useGetCollectionSiteFromNamesQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: slug ?? "",
    },
    pause: slug == null || tagLevel === "org",
  });
  const [{ data: orgTagsData, fetching: orgTagsFetching }] =
    useOrganisationTagsQuery({
      variables: {
        organisationSubdomain: subdomain,
        filters: { ...tagFilters, hasCollectionSiteRelation: { neq: true } },
      },
      pause: tagLevel === "site",
    });
  const [{ data: siteTagsData, fetching: siteTagsFetching }] =
    useCollectionSiteTagsQuery({
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: slug ?? "",
        filters: tagFilters,
        orgFilters: { ...tagFilters, hasCollectionSiteRelation: { neq: true } },
      },
      pause: slug == null || tagLevel === "org",
    });

  const handleChange = (tag: TagOptionZod) => {
    if (value?.some((u) => u.id === tag.id) === true) {
      onChange(value.filter((u) => u.id !== tag.id));
    } else {
      onChange([...(value ?? []), tag]);
    }
    setOpen(false);
  };

  const handleCreateTag = async (name: string) => {
    const site = siteData?.org?.site;
    if (tagLevel === "site" && site == null) {
      toast.error("Site not found");
      return;
    }

    const { data } = await createTag({
      input: {
        collectionSiteId: site?.id ?? null,
        name,
      },
    });

    const res = data?.createRecordTag;
    const error = res?.errors?.[0]?.__typename;

    if (error === "DuplicateNameError" || error === "SiteTagMergeError") {
      toast.error("Tag already exists");
    } else if (error != null) {
      toast.error("Failed to create tag");
    }

    if (res?.success === true && res.recordTag != null) {
      onChange([
        ...(value ?? []),
        { id: res.recordTag.id, name: res.recordTag.name },
      ]);
      setSearch("");
      setOpen(false);
      toast.success("Tag created", { description: res.recordTag.name });
    }
  };

  const tags =
    tagLevel === "site"
      ? [
          ...(siteTagsData?.org?.tags?.nodes ?? []),
          ...(siteTagsData?.org?.site?.tags?.nodes ?? []),
        ]
      : orgTagsData?.org?.tags?.nodes;

  const fetching = tagLevel === "site" ? siteTagsFetching : orgTagsFetching;

  const showNewTagOption =
    newTagOption &&
    tags != null &&
    search !== "" &&
    !tags.some((tag) => tag.name.toLowerCase() === search.toLowerCase());

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      {fetching && <CommandLoading />}
      <CommandList className="px-0 py-0">
        <div className="divide-y divide-grey-200">
          {tags?.length !== 0 && (
            <CommandGroup className="p-1">
              {tags?.map((tag) => {
                const selected = value?.some((t) => t.id === tag.id) === true;
                return (
                  <CommandItem
                    key={tag.id}
                    value={tag.id}
                    keywords={[tag.name]}
                    onSelect={() => {
                      handleChange({
                        id: tag.id,
                        name: tag.name,
                      });
                      setSearch("");
                    }}
                    data-cy="tag-picker-item"
                    selected={selected}
                  >
                    <span className="truncate">{tag.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {showNewTagOption && (
            <CommandGroup className="border-grey-200 p-1 pb-0">
              <CommandItem
                value="new-tag"
                onSelect={() => {
                  void handleCreateTag(search);
                }}
                className="flex"
                data-cy="new-tag-item"
                loading={createTagFetching}
              >
                <Plus className="shrink-0 text-grey-500" />
                <div>
                  New tag
                  {search !== "" && (
                    <>
                      :{" "}
                      <span className="flex-wrap break-all font-normal text-grey-500">
                        &apos;{search}&apos;
                      </span>
                    </>
                  )}
                </div>
              </CommandItem>
            </CommandGroup>
          )}
        </div>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          data-cy={dataCy}
          asChild
          onFocusCapture={stopEventPropagation}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent className="z-99999 min-w-(--radix-popover-trigger-width) max-w-sm">
          <Command
            filter={(value, search, keywords) => {
              const extendValue = keywords?.join(" ") ?? "";
              if (
                extendValue.toLowerCase().includes(search.toLowerCase()) ||
                value === "new-tag"
              ) {
                return 1;
              }
              return 0;
            }}
          >
            <CommandInput
              placeholder={placeholder ?? "Pick tags..."}
              value={search}
              onValueChange={setSearch}
              data-cy="tag-picker-input"
            />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        data-cy={dataCy}
        asChild
        onFocusCapture={stopEventPropagation}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-none p-0!">
        <Command
          size="lg"
          filter={(value, search, keywords) => {
            const extendValue = keywords?.join(" ") ?? "";
            if (
              extendValue.toLowerCase().includes(search.toLowerCase()) ||
              value === "new-tag"
            ) {
              return 1;
            }
            return 0;
          }}
        >
          <CommandInput
            placeholder={placeholder ?? "Pick tags..."}
            value={search}
            onValueChange={setSearch}
            data-cy="tag-picker-input"
          >
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandInput>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
